/**
 * useMediaUpload — React hook for uploading images to R2 via Edge Function.
 * Handles loading state, error, and returns the public URL on success.
 */

import { useState, useCallback } from 'react'
import { uploadMedia, uploadMaterial } from '@turnifly/shared/api'
import type { ImageCategory, UploadResult } from '@turnifly/shared/api'

interface UseMediaUploadReturn {
  upload: (file: File, category: ImageCategory) => Promise<UploadResult>
  uploadFile: (file: File) => Promise<UploadResult>
  isUploading: boolean
  error: string | null
  reset: () => void
}

export function useMediaUpload(): UseMediaUploadReturn {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upload = useCallback(
    async (file: File, category: ImageCategory): Promise<UploadResult> => {
      setIsUploading(true)
      setError(null)
      try {
        const result = await uploadMedia(file, category)
        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed'
        setError(message)
        throw err
      } finally {
        setIsUploading(false)
      }
    },
    [],
  )

  const uploadFile = useCallback(
    async (file: File): Promise<UploadResult> => {
      setIsUploading(true)
      setError(null)
      try {
        const result = await uploadMaterial(file)
        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed'
        setError(message)
        throw err
      } finally {
        setIsUploading(false)
      }
    },
    [],
  )

  const reset = useCallback((): void => {
    setError(null)
    setIsUploading(false)
  }, [])

  return { upload, uploadFile, isUploading, error, reset }
}
